import { useMemo } from "react";
import { IOption } from "components/MainSelect/MainSelectInterfaces";
import { IUseExtraFilters } from "./useExtraFiltersInterfaces";
import { UsersEntity } from "entities/Users/UsersEntity";
import { ShiftEntity } from "entities/Shifts/ShiftEntity";
import { ProductionLinesEntity } from "entities/ProductionLines/ProductionLinesEntity";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { ASSIGNABLE_USER_TYPE_IDS, USERTYPE } from "constants/constants";
import { useTranslation } from "react-i18next";
import { handleNameTranslation } from "utilities/localesUtilities";

const useExtraFilters = ({ filtersSelected, extraFiltersSelected }: IUseExtraFilters) => {
    const { users } = UsersEntity.GetAllUsers(ASSIGNABLE_USER_TYPE_IDS);
    const { shifts } = ShiftEntity.GetShiftList();
    const { productionLines } = ProductionLinesEntity.GetAllProductionLines();
    const { machineCenters } = MachineCentersEntity.GetAllMachineCenters();
    const { functionalLocations } = MachineCentersEntity.GetAllFunctionalLocations();
    const { keySuccessFactors } = CorporationsEntity.GetAllKeySuccessFactors();
    const { failureTypes } = CorporationsEntity.GetAllFailureTypes();

    const { i18n } = useTranslation();

    const filteredUsersByMillsSelected = useMemo(() => {
        if (filtersSelected.selectedAllMillsIds.length > 0) {
            return users.filter(
                (user) =>
                    user.user_type.user_type_id === USERTYPE.ADMINISTRATOR ||
                    user.user_type.user_type_id === USERTYPE.EXECUTIVE ||
                    filtersSelected.selectedAllMillsIds.includes(user.mill?.mill_id!),
            );
        }
        return users.filter(
            (user) =>
                (filtersSelected.millsIds.length > 0
                    ? user.user_type.user_type_id === USERTYPE.ADMINISTRATOR ||
                      user.user_type.user_type_id === USERTYPE.EXECUTIVE
                    : false) || (filtersSelected.millsIds as number[]).includes(user.mill?.mill_id!),
        );
    }, [filtersSelected.millsIds, filtersSelected.selectedAllMillsIds, users]);

    const ownersList = useMemo(() => {
        if (filteredUsersByMillsSelected.length === 0) return [];
        const currentFilteredUsersByMillsSelected = [...filteredUsersByMillsSelected];
        const orderedFilteredUsersByMillsSelected = currentFilteredUsersByMillsSelected.sort((a, b) => {
            if (a.first_name.toLowerCase() < b.first_name.toLowerCase()) {
                return -1;
            }
            if (a.first_name.toLowerCase() > b.first_name.toLowerCase()) {
                return 1;
            }
            return 0;
        });
        const ownersInitialOptions = orderedFilteredUsersByMillsSelected.map((owner) => ({
            id: owner.user_id,
            name: `${owner.first_name} ${owner.last_name}`,
            picture: owner.media?.media_url || "",
        })) as IOption[];

        return ownersInitialOptions;
    }, [filteredUsersByMillsSelected]);

    const filteredShiftsByMillsSelected = useMemo(() => {
        if (shifts.length === 0) return [];
        if (filtersSelected.selectedAllMillsIds.length > 0) {
            return shifts.filter((shift) => filtersSelected.selectedAllMillsIds.includes(shift.mill_id!));
        }
        return shifts.filter((shift) => (filtersSelected.millsIds as number[]).includes(shift.mill_id!));
    }, [filtersSelected.millsIds, filtersSelected.selectedAllMillsIds, shifts]);

    const shiftsList = useMemo(() => {
        const shiftsInitialOptions = filteredShiftsByMillsSelected.map((shift) => ({
            id: shift.shift_id,
            name: shift.name,
        })) as IOption[];

        return shiftsInitialOptions;
    }, [filteredShiftsByMillsSelected]);

    const filteredProductionLinesByMillsSelected = useMemo(() => {
        if (productionLines.length === 0) return [];
        if (filtersSelected.selectedAllMillsIds.length > 0) {
            return productionLines.filter((productionLine) =>
                filtersSelected.selectedAllMillsIds.includes(productionLine.mill?.mill_id!),
            );
        }
        return productionLines.filter((productionLine) =>
            (filtersSelected.millsIds as number[]).includes(productionLine.mill?.mill_id!),
        );
    }, [filtersSelected.millsIds, filtersSelected.selectedAllMillsIds, productionLines]);

    const productionLinesList = useMemo(() => {
        const productionLinesInitialOptions = filteredProductionLinesByMillsSelected.map((productionLine) => ({
            id: productionLine.production_line_id,
            name: productionLine.name,
        })) as IOption[];

        return productionLinesInitialOptions;
    }, [filteredProductionLinesByMillsSelected]);

    const filteredMachineCentersByMillsSelected = useMemo(() => {
        if (machineCenters.length === 0) return [];

        return machineCenters.filter((machineCenter) =>
            extraFiltersSelected.productionLineId.includes(machineCenter.production_line_id!),
        );
    }, [extraFiltersSelected.productionLineId, machineCenters]);

    const machineCentersList = useMemo(() => {
        const machineCentersInitialOptions = filteredMachineCentersByMillsSelected.map((machineCenter) => ({
            id: machineCenter.machine_center_id,
            name: machineCenter.name,
            tag: machineCenter.production_line.name,
        })) as IOption[];

        return machineCentersInitialOptions;
    }, [filteredMachineCentersByMillsSelected]);

    const filteredFunctionalLocationsByMillsSelected = useMemo(() => {
        if (functionalLocations.length === 0) return [];
        if (productionLines.length === 0) return [];
        if (machineCenters.length === 0) return [];

        return functionalLocations.filter((functionalLocation) =>
            extraFiltersSelected.machineCenterId.includes(functionalLocation.area?.machine_center?.machine_center_id!),
        );
    }, [extraFiltersSelected.machineCenterId, functionalLocations, machineCenters.length, productionLines.length]);

    const functionalLocationsList = useMemo(() => {
        const functionalLocationsInitialOptions = filteredFunctionalLocationsByMillsSelected.map(
            (functionalLocation) => ({
                id: functionalLocation.functional_location_id,
                name: handleNameTranslation(i18n.language, functionalLocation.translation.translation_names),
                tag: functionalLocation.area?.machine_center?.name,
            }),
        ) as IOption[];

        return functionalLocationsInitialOptions;
    }, [filteredFunctionalLocationsByMillsSelected, i18n.language]);

    const filteredKeySuccessFactorsByMillsSelected = useMemo(() => {
        if (keySuccessFactors.length === 0) return [];
        return keySuccessFactors;
    }, [keySuccessFactors]);

    const keySuccessFactorsList = useMemo(() => {
        const keySuccessFactorsInitialOptions = filteredKeySuccessFactorsByMillsSelected.map((keySuccessFactor) => ({
            id: keySuccessFactor.key_success_factors_id,
            name: keySuccessFactor.name,
        })) as IOption[];

        return keySuccessFactorsInitialOptions;
    }, [filteredKeySuccessFactorsByMillsSelected]);

    const filteredFailureTypesByMillsSelected = useMemo(() => {
        if (failureTypes.length === 0) return [];
        return failureTypes;
    }, [failureTypes]);

    const failureTypesList = useMemo(() => {
        const failureTypesInitialOptions = filteredFailureTypesByMillsSelected.map((failureType) => ({
            id: failureType.failure_type_id,
            name: failureType.name,
        })) as IOption[];

        return failureTypesInitialOptions;
    }, [filteredFailureTypesByMillsSelected]);

    return {
        ownersList,
        shiftsList,
        productionLinesList,
        machineCentersList,
        functionalLocationsList,
        keySuccessFactorsList,
        failureTypesList,
    };
};

export default useExtraFilters;
